import { useCallback, useState } from 'react'
import { useFetcher, useSearchParams } from 'react-router-dom'

import { DELETE_PROJECT } from '~/entities/projects'

import { useNavigate } from '~/shared/lib/reactRouterWrappers'

import { Box } from '~/components/Box'
import { Button } from '~/components/Button'
import { Card } from '~/components/Card'
import { Grid } from '~/components/Grid'
import { CheckCircle, Info } from '~/components/Icons'
import { Input } from '~/components/Input'
import { Message } from '~/components/Message'
import { Modal } from '~/components/Modal'
import { Stack } from '~/components/Stack'
import { Text } from '~/components/Text'

export type DeleteProjectData = {
  projectId: string
  projectSlug: string
  isOrgAccount: boolean
  isProjectOwner: boolean
  isHepicActive: boolean
}

export const DeleteProject = ({
  projectSlug,
  isOrgAccount,
  isProjectOwner,
  isHepicActive,
  projectId,
}: DeleteProjectData) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [confirmation, setConfirmation] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')

  const fetcher = useFetcher()
  const isDeletingProject = searchParams.get('action') === 'delete-project-' + projectId

  const warnings = {
    isOrgAccount: 'Deleting a project will remove all its data and users.',
    isProjectOwner: 'Only project owners can delete a project.',
    isHepicActive:
      'Your project has an active Hepic integration. Please request Hepic is disabled from tickets@gigapipe.com before deleting the project',
  }
  const warningMessages = Object.entries(warnings)
    .filter(([key]) => {
      if (key === 'isOrgAccount') return !isOrgAccount
      if (key === 'isProjectOwner') return !isProjectOwner
      if (key === 'isHepicActive') return isHepicActive
    })
    .map(([_, message]) => message)

  const onConfirmationTextChange = useCallback(
    (text: string) => {
      setConfirmationText(text)
      if (text === projectSlug) {
        setConfirmation(true)
      }
      if (text !== projectSlug) {
        setConfirmation(false)
      }
    },
    [projectSlug],
  )

  return (
    <Grid space="medium" columns={12} align="flex-end">
      <Grid.Item span={{ desktop: 12, tablet: 12, mobile: 12 }}>
        <Card fill>
          <Card.Header title="Delete project" />
          <Card.Section>
            <Stack direction="horizontal" justify="between" align="center">
              <Box>
                {warningMessages.map((message, index) => (
                  <Text key={index} as="p" lineHeight={'large'}>
                    <Info color="orange" /> {message}
                    {index < warningMessages.length - 1 && <br />}
                  </Text>
                ))}
                {warningMessages.length === 0 && (
                  <>
                    <Text as="p" lineHeight={'large'}>
                      Deleting a project will remove all its data and users.
                    </Text>
                    <Text as="p" lineHeight={'large'}>
                      This operation cannot be undone.
                    </Text>
                  </>
                )}
              </Box>
              <Box>
                {/* we should add a confirm modal as we have with delete user and delete organization */}
                <>
                  <Button
                    aria-label="Delete Project"
                    color="brand"
                    isLoading={fetcher.state === 'submitting'}
                    isDisabled={fetcher.state === 'submitting' || isHepicActive || !isProjectOwner}
                    onPress={() => {
                      return navigate('', {
                        searchParams: { action: `delete-project-${projectId}` },
                        preventScrollReset: true,
                      })
                    }}
                  >
                    Delete Project
                  </Button>
                  <Modal
                    isOpen={isDeletingProject}
                    onClose={() => navigate('.', { preventScrollReset: true })}
                    dialogProps={{ id: `delete-project-${projectId}` }}
                  >
                    <Modal.Header
                      title={`Are you sure you want to delete the selected project? (${projectSlug})`}
                    />
                    <Modal.Content>
                      {Boolean(fetcher.data?.errors?.fieldErrors.id) && (
                        <Message type="error" size="small">
                          {fetcher.data?.errors?.fieldErrors.id}
                        </Message>
                      )}
                      {/* <Box marginTop="large">
                        <Checkbox
                          name="confirmation"
                          id="confirmation"
                          isSelected={confirmation}
                          onChange={e => {
                            setConfirmation(() => e)
                          }}
                        >
                          <Text size="small" color="orange500">
                            I understand that this action cannot be undone.
                          </Text>
                        </Checkbox>
                      </Box> */}
                      <Box marginTop="medium">
                        <Text size="medium" color="gray200">
                          This is a permanent action and cannot be undone. All data associated with
                          this project will not be readable and all data sources will be removed
                          from Grafana.
                        </Text>
                      </Box>
                      <Box marginTop="medium">
                        <Text size="medium" color="gray100">
                          Grafana users will NOT be affected by this. If you wish to delete them
                          from Grafana too then please do so in Grafana/users itself.
                        </Text>
                      </Box>
                      <Box marginTop="medium">
                        <Text size="small" color="gray200" lineHeight={'large'}>
                          {' '}
                          Please confirm you are sure you wish to delete the project {
                            projectSlug
                          }{' '}
                          by typing{' '}
                          <span style={{ fontWeight: 'bold', color: 'orange' }}>
                            {' '}
                            {projectSlug}
                          </span>{' '}
                          in the box below
                        </Text>
                      </Box>

                      <Box marginTop="small">
                        <Input
                          name="confirmationText"
                          aria-label="confirmationText"
                          id="confirmationText"
                          type="text"
                          onChange={onConfirmationTextChange}
                          value={confirmationText}
                        />
                        {!confirmation && (
                          <Text color="red500" size="small">
                            Project name does not match
                          </Text>
                        )}

                        <Box
                          marginTop="small"
                          display="flex"
                          flexDirection={'row'}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Text size="medium" color="gray600">
                            {' '}
                            <span
                              style={
                                confirmation
                                  ? { color: 'lightgreen', fontWeight: 'bold' }
                                  : { color: 'red', fontWeight: 'bold' }
                              }
                            >
                              {confirmationText} {confirmation ? <CheckCircle /> : null}
                            </span>
                          </Text>
                        </Box>
                      </Box>
                    </Modal.Content>
                    <Modal.Footer>
                      <Modal.Action
                        autoFocus
                        onPress={() => navigate('.', { preventScrollReset: true })}
                      >
                        <Text color="gigagreen">Cancel</Text>
                      </Modal.Action>
                      <Modal.Action
                        isDisabled={fetcher.state === 'submitting' || !confirmation}
                        onPress={() => {
                          return fetcher.submit(
                            {
                              intent: DELETE_PROJECT,
                              id: projectId,
                              projectSlug: projectSlug,
                            },
                            {
                              method: 'post',
                              action: `/${projectSlug}/settings`,
                            },
                          )
                        }}
                      >
                        <Text color="gigagreen">Confirm</Text>
                      </Modal.Action>
                    </Modal.Footer>
                  </Modal>
                </>
              </Box>
            </Stack>
          </Card.Section>
        </Card>
      </Grid.Item>
    </Grid>
  )
}
